import { useEffect, useMemo, useState } from "react";
import type { DictionaryEntry, RawDictionaryEntry } from "../types";

type DictionaryState = {
  entries: DictionaryEntry[];
  status: "idle" | "loading" | "ready" | "error";
  error?: string;
  posList: string[];
};

const DICTIONARY_FILE = "dictionary.json";

export function useDictionary(): DictionaryState {
  const [entries, setEntries] = useState<DictionaryEntry[]>([]);
  const [status, setStatus] = useState<DictionaryState["status"]>("idle");
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    const loadDictionary = async () => {
      setStatus("loading");
      try {
        const dictionaryUrl = new URL(DICTIONARY_FILE, window.location.href).toString();
        const response = await fetch(dictionaryUrl, { cache: "force-cache" });
        if (!response.ok) {
          throw new Error(`Unable to fetch dictionary (${response.status})`);
        }

        const payload = (await response.json()) as Record<string, RawDictionaryEntry>;
        const parsed: DictionaryEntry[] = Object.entries(payload)
          .map(([rank, entry]) => ({
            ...entry,
            rank: Number(rank),
            concerns: entry.concerns ?? []
          }))
          .sort((a, b) => a.rank - b.rank);

        if (!cancelled) {
          setEntries(parsed);
          setStatus("ready");
        }
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      }
    };

    loadDictionary();
    return () => {
      cancelled = true;
    };
  }, []);

  const posList = useMemo(
    () =>
      Array.from(
        entries.reduce((acc, entry) => {
          acc.add(entry.pos);
          return acc;
        }, new Set<string>())
      ).sort((a, b) => a.localeCompare(b)),
    [entries]
  );

  return { entries, status, error, posList };
}
